/**
 * Detects JWT access-token expiry from API error payloads.
 *
 * Asset-manager and other internal services may return any of:
 *   { err: { message: 'jwt expired' } }
 *   { code: 'TOKEN_EXPIRED', message: 'Token expired', err: { name: 'TokenExpiredError', message: 'jwt expired' } }
 *   { detail: 'Signature has expired: ...' }  (YouRAI/FastAPI)
 */
function parseErrorBody(data: unknown): Record<string, unknown> | null {
  if (data == null) return null;
  if (typeof data === 'string') {
    try {
      const parsed = JSON.parse(data) as unknown;
      return typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)
        ? (parsed as Record<string, unknown>)
        : null;
    } catch {
      return null;
    }
  }
  if (typeof data === 'object' && !Array.isArray(data)) {
    return data as Record<string, unknown>;
  }
  return null;
}

function errFieldMessage(err: unknown): string | undefined {
  if (err == null) return undefined;
  if (typeof err === 'string') {
    return err.toLowerCase().includes('jwt expired') || err.includes('TokenExpiredError')
      ? 'jwt expired'
      : undefined;
  }
  if (Array.isArray(err)) {
    for (const item of err) {
      const msg = errFieldMessage(item);
      if (msg) return msg;
    }
    return undefined;
  }
  if (typeof err === 'object' && 'message' in err) {
    const msg = (err as { message: unknown }).message;
    return typeof msg === 'string' ? msg : undefined;
  }
  return undefined;
}

function errFieldName(err: unknown): string | undefined {
  if (err == null || typeof err !== 'object' || !('name' in err)) return undefined;
  const name = (err as { name: unknown }).name;
  return typeof name === 'string' ? name : undefined;
}

/**
 * True for permission / authorization failures (403 Forbidden, etc.).
 * These must not trigger JWT refresh or logout — surface the message to the user instead.
 */
export function isAuthorizationDeniedError(data: unknown, httpStatus?: number): boolean {
  if (isExpiredJwtAuthError(data)) return false;

  const body = parseErrorBody(data);
  const status =
    httpStatus ?? (typeof body?.statusCode === 'number' ? (body.statusCode as number) : undefined);

  if (status === 403) return true;

  if (!body) return false;

  const topMessage = typeof body.message === 'string' ? body.message.toLowerCase() : '';
  const errorField = typeof body.error === 'string' ? body.error.toLowerCase() : '';

  if (errorField === 'forbidden') return true;
  if (topMessage.includes('permission') || topMessage.includes('forbidden')) return true;
  if (topMessage.includes("don't have permission") || topMessage.includes('do not have permission')) {
    return true;
  }

  return false;
}

/**
 * Whether an HTTP client should attempt JWT refresh + retry for this response.
 * Only true for explicit JWT expiry signals — never for permission denied (403 Forbidden).
 */
export function shouldAttemptJwtRefresh(httpStatus: number | undefined, data: unknown): boolean {
  if (!httpStatus) return false;
  if (isAuthorizationDeniedError(data, httpStatus)) return false;
  return (httpStatus === 401 || httpStatus === 403) && isExpiredJwtAuthError(data);
}

/** True when an HTTP response body indicates the bearer JWT has expired (refresh may help). */
export function isExpiredJwtAuthError(data: unknown): boolean {
  const body = parseErrorBody(data);
  if (!body) {
    if (typeof data === 'string') {
      const lower = data.toLowerCase();
      return (
        data.includes('"jwt expired"') ||
        lower.includes('token expired') ||
        data.includes('TokenExpiredError')
      );
    }
    return false;
  }

  const errMessage = errFieldMessage(body.err);
  if (errMessage === 'jwt expired') return true;

  const errName = errFieldName(body.err);
  if (errName === 'TokenExpiredError') return true;

  const topMessage = typeof body.message === 'string' ? body.message : undefined;
  if (topMessage === 'jwt expired') return true;
  if (topMessage?.toLowerCase() === 'token expired') return true;

  const code = typeof body.code === 'string' ? body.code : undefined;
  if (code === 'TOKEN_EXPIRED') return true;

  const detail = typeof body.detail === 'string' ? body.detail : undefined;
  if (detail?.toLowerCase().includes('signature has expired')) return true;

  return false;
}

/**
 * True when OUR OWN bearer token is already past its `exp`, read straight off
 * the token rather than off a server answer.
 *
 * Needed because an expired-token 401 does not always reach JavaScript. The
 * YouRAI API emits its auth failures without `Access-Control-Allow-Origin`
 * (verified against QA: a 200 carries `access-control-allow-origin: *`, the
 * 401 carries no CORS header at all), so the browser blocks the response and
 * axios reports a bare "Network Error" with no `response` and no status. Every
 * status-driven refresh check then declines to refresh, the session is never
 * renewed, and every request from that point on fails the same way until the
 * page is reloaded — which is how one hour of editing turns into "nothing
 * saves any more".
 *
 * Reading the token's own expiry recovers the signal the CORS policy removed.
 *
 * @param leewaySeconds treat a token expiring this soon as already expired, so
 * a request in flight across the boundary refreshes instead of racing it.
 */
export function isJwtPastExpiry(jwt: unknown, leewaySeconds = 30): boolean {
  if (typeof jwt !== 'string' || !jwt) return false;
  const payload = jwt.split('.')[1];
  if (!payload) return false;
  try {
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    const exp = (JSON.parse(json) as { exp?: unknown }).exp;
    if (typeof exp !== 'number') return false;
    return exp * 1000 <= Date.now() + leewaySeconds * 1000;
  } catch {
    // An unreadable token is not evidence of expiry — leave it to the server.
    return false;
  }
}

/**
 * True for a failure that carries no HTTP response at all AND whose bearer is
 * already expired. That combination has exactly one common cause: an auth
 * rejection the browser refused to show us. Refresh-and-retry is the right
 * response; reporting a network outage is not.
 */
export function isCorsMaskedAuthFailure(error: unknown, jwt: unknown): boolean {
  const hasResponse = Boolean((error as { response?: unknown } | null)?.response);
  if (hasResponse) return false;
  return isJwtPastExpiry(jwt);
}

/** True when a raw response body string indicates JWT expiry (SSE / fetch paths). */
export function isExpiredJwtAuthBody(body: string): boolean {
  if (!body) return false;
  if (isExpiredJwtAuthError(body)) return true;
  const lower = body.toLowerCase();
  return (
    body.includes('"jwt expired"') || lower.includes('token expired') || body.includes('TokenExpiredError')
  );
}

/** True when the backend returned 401 GuestAccessDenied — the visitor is unauthenticated
 *  and must log in to proceed. */
export function isGuestAccessDenied(err: unknown): boolean {
  const response = (err as { response?: { status?: number; data?: { detail?: { name?: string } } } })
    ?.response;
  return response?.status === 401 && response?.data?.detail?.name === 'GuestAccessDenied';
}

/** GraphQL errorLink: extensions.code + message combinations that mean access JWT expired. */
export function isExpiredJwtGraphQLError(err: {
  message?: string;
  extensions?: { code?: string } | null;
}): boolean {
  const code = err.extensions?.code;
  const message = err.message ?? '';
  if (code === 'UNAUTHENTICATED' && message === 'jwt expired') return true;
  if (code === 'TOKEN_EXPIRED') return true;
  if (message === 'jwt expired') return true;
  if (message.toLowerCase() === 'token expired') return true;
  return false;
}
